<script setup lang="ts">
import { computed } from 'vue'

import { pluralize } from '@/helpers/StringHelpers'

export type SelectionStatusBarProps = {
  /** Current number of selected items */
  selectedCount: number
  /** Minimum number of selections required for validity */
  min?: number
  /** Maximum number of selections allowed (use Infinity for unlimited) */
  max?: number
  /** Singular label for items in status messages (e.g., "option") */
  itemLabel?: string
  /** Plural label for items in status messages (e.g., "options") */
  itemLabelPlural?: string
  /** Error state */
  isError?: boolean
}

const props = withDefaults(defineProps<SelectionStatusBarProps>(), {
  selectedCount: 0,
  min: 0,
  max: Infinity,
  itemLabel: 'option',
  itemLabelPlural: 'options',
})

const hasMax = computed(() => props.max < Infinity)

type Variant = 'error' | 'success' | 'default'

const variant = computed((): Variant => {
  const { selectedCount, min, max, isError } = props

  if (isError) return 'error'

  const isValid = selectedCount >= min && selectedCount <= max

  return isValid ? 'success' : 'default'
})

type ConstraintType = 'exact' | 'min-max' | 'min-only' | 'max-only' | 'none'

const constraintType = computed((): ConstraintType => {
  const { min, max } = props

  if (min) {
    if (min === max) return 'exact'
    if (hasMax.value) return 'min-max'
    return 'min-only'
  }

  if (hasMax.value) return 'max-only'

  return 'none'
})

const statusText = computed(() => {
  const { min, max, itemLabel, itemLabelPlural } = props

  const minNoun = pluralize(min, itemLabel, itemLabelPlural)
  const maxNoun = pluralize(max, itemLabel, itemLabelPlural)

  switch (constraintType.value) {
    case 'exact':
      return `Select ${min} ${minNoun}`
    case 'min-max':
      return `Select ${min}-${max} ${maxNoun}`
    case 'min-only':
      return `Select at least ${min} ${minNoun}`
    case 'max-only':
      return `Select up to ${max} ${maxNoun}`
    default:
      return `Select your ${itemLabelPlural}`
  }
})

const accessibleErrorText = computed(() => {
  const { isError, selectedCount, itemLabel, itemLabelPlural } = props
  const noun = pluralize(selectedCount, itemLabel, itemLabelPlural)
  if (isError) return `Error: ${statusText.value}. Currently ${selectedCount} ${noun} selected.`
  return ''
})
</script>

<template>
  <div class="selection-status-bar" :class="variant">
    <span aria-atomic="true" class="visually-hidden" role="status">{{ accessibleErrorText }}</span>

    <div class="text">{{ statusText }}</div>

    <div class="count">
      <span class="current">{{ selectedCount }}</span>
      <span v-if="hasMax" class="max"> / {{ max }} </span>
      <span class="tail">selected</span>
    </div>
  </div>
</template>

<style scoped>
.selection-status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-0-5) var(--space-1);
  border-left-width: 3px;
  border-left-style: solid;
  border-left-color: var(--neutral-border-color);
  border-radius: var(--radius-small);
  gap: var(--space-1);
  color: var(--text-secondary);
  background-color: var(--elevation-0);

  &.success {
    color: var(--success-text-color);
    background: var(--success-background-color);
    border-left-color: var(--success-border-color-strong);
  }

  &.warning {
    color: var(--warning-text-color);
    background: var(--warning-background-color);
    border-left-color: var(--warning-border-color-strong);
  }

  &.error {
    color: var(--error-text-color);
    background: var(--error-background-color);
    border-left-color: var(--error-border-color-strong);
  }

  .text {
    flex: 1;
    font-size: var(--size-sm);
    font-weight: var(--font-weight-semibold);
  }

  .count {
    display: flex;
    align-items: baseline;
    gap: var(--space-0-25);
    font-weight: var(--font-weight-semibold);
    white-space: nowrap;

    .current {
      font-size: var(--size-lg);
      color: var(--text-info);
      line-height: 1;
    }

    .max {
      font-size: var(--size-md);
      line-height: 1;
    }

    .tail {
      font-size: var(--size-xs);
      font-weight: var(--font-weight-regular);
      text-transform: lowercase;
    }
  }
}
</style>
